import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "personal-injury-lawyer-web-design-colorado",
  metaTitle: "Personal Injury Lawyer Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom personal injury lawyer web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Personal Injury Lawyer Web Design Colorado",
  eyebrow: "Personal Injury Lawyer Web Design — Colorado",
  h1: (
    <>
      Personal Injury Lawyer <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded personal injury lawyer websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Personal Injury Lawyer websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first personal injury lawyer sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Personal Injury Lawyer web design in Colorado",
    body: "Personal Injury Lawyer web design is the practice of building conversion-focused, mobile-first websites for personal injury lawyer businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — PI searches happen on the side of the road." },
    { title: "Case-type service pages", body: "Auto, truck, motorcycle, slip-and-fall, dog bite, wrongful death — each ranks independently." },
    { title: "Free case evaluation forms", body: "Short qualifying intake with auto-routing to intake staff during business hours." },
    { title: "Sticky click-to-call", body: "24/7 click-to-call on every scroll, every page." },
    { title: "Verdicts & settlements", body: "Properly disclaimed results pages that build authority without bar violations." },
    { title: "Schema and SEO at launch", body: "LegalService, Attorney, Review, FAQPage schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Speed wins the PI lead", body: "PI is a sign-up-in-24-hours business. A slow site or generic form loses the lead to the next firm." },
    { heading: "Case-type pages are the ranking moat", body: "Auto, truck, and motorcycle cases are different searches, different settlements, different SERPs." },
    { heading: "Reviews and verdicts close the call", body: "Above-the-fold Google reviews and disclaimed verdict highlights drive intake conversion." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a personal injury lawyer website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a personal injury lawyer website?", a: "Typical websites are built in less than 7 business days. Larger sites, migrations, and custom integrations are scoped before work starts." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs personal injury lawyer businesses?", a: "No — we build personal injury lawyer sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Personal Injury Lawyer Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for personal injury lawyer businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/personal-injury-lawyer-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
